import { format } from 'date-fns'
import { CalendarPlus, Clock, ExternalLink } from 'lucide-react'
import { useState } from 'react'

import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPES_BY_ID,
  getClinicTimezone,
  isAppointmentTypeAvailableAtClinic,
} from '@snootsvet/shared'

import { Alert, AlertAction, AlertTitle } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/frontend/components/ui/combobox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { useAppointmentCalendarContext } from '@/frontend/features/appointments/appointment-calendar-provider'
import { BlockedSlotForm } from '@/frontend/features/appointments/blocked-slot.form'
import { BookAppointmentDialog } from '@/frontend/features/appointments/book-appointment-dialog'

export function AppointmentCalendarHeaderActions() {
  const { state, availability, requestAvailability, cancelAvailabilityRequest } =
    useAppointmentCalendarContext()
  const { clinicId, staffIds } = state
  const [portalDialogOpen, setPortalDialogOpen] = useState(false)
  const [blockDialogOpen, setBlockDialogOpen] = useState(false)
  const appointmentTypes = APPOINTMENT_TYPES.filter((type) =>
    clinicId ? isAppointmentTypeAvailableAtClinic(type.id, clinicId) : true
  ).sort((a, b) => a.name.localeCompare(b.name))

  return (
    <>
      {availability ? (
        <Alert
          variant="success"
          role="status"
          className="ml-auto w-[32rem] max-w-full min-h-9 content-center"
        >
          <AlertTitle>
            {availability.type === 'rescheduling'
              ? `Rescheduling ${availability.appointment.appointmentTypeName} for ${availability.appointment.patient.name}…`
              : `Booking new ${APPOINTMENT_TYPES_BY_ID[availability.appointment.appointmentTypeId].name} appointment…`}
          </AlertTitle>
          <AlertAction className="top-1/2 -translate-y-1/2">
            <Button variant="outline" onClick={cancelAvailabilityRequest}>
              Cancel
            </Button>
          </AlertAction>
        </Alert>
      ) : (
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <Button
            size="lg"
            variant="outline"
            disabled={!clinicId}
            onClick={() => setBlockDialogOpen(true)}
          >
            <Clock className="size-4" />
            Block time
          </Button>
          <Button size="lg" variant="outline" onClick={() => setPortalDialogOpen(true)}>
            <ExternalLink className="size-4" />
            Book via Portal
          </Button>
          <Combobox<(typeof APPOINTMENT_TYPES)[number]>
            items={appointmentTypes}
            value={null}
            onValueChange={(type) =>
              type &&
              requestAvailability({ type: 'booking', appointment: { appointmentTypeId: type.id } })
            }
            itemToStringLabel={(type) => type.name}
            disabled={!clinicId}
          >
            <ComboboxTrigger render={<Button disabled={!clinicId} size="lg" />}>
              <CalendarPlus className="size-4" />
              Book Appointment
            </ComboboxTrigger>
            <ComboboxContent className="w-80" align="end">
              <ComboboxInput
                aria-label="Search appointment types"
                placeholder="Search appointment types…"
                showTrigger={false}
              />
              <ComboboxEmpty>No appointment types found.</ComboboxEmpty>
              <ComboboxList>
                <ComboboxCollection>
                  {(type) => (
                    <ComboboxItem key={type.id} value={type}>
                      <span className="flex-1">{type.name}</span>
                      <span className="text-muted-foreground">{type.duration} min</span>
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>
      )}

      <BookAppointmentDialog
        open={portalDialogOpen}
        onOpenChange={setPortalDialogOpen}
        defaultClinicId={clinicId}
      />

      {clinicId && (
        <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Block time</DialogTitle>
            </DialogHeader>
            <BlockedSlotForm
              defaultValues={{
                staffId: staffIds.length === 1 ? staffIds[0] : undefined,
                clinicId,
                startDate: format(new Date(), 'yyyy-MM-dd'),
                startTime: '09:00',
                endDate: format(new Date(), 'yyyy-MM-dd'),
                endTime: '17:00',
                note: '',
              }}
              timeZone={getClinicTimezone(clinicId)}
              onSuccess={() => setBlockDialogOpen(false)}
              onCancel={() => setBlockDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
